import { setTraits, ChildOf, Pair, Transform, type Plugin, type State, type System } from '@multiplekex/shallot';
import { Line, Text } from '@multiplekex/shallot/extras';
import { LINE } from './colors';

export const Border = {
  padding: [] as number[],
  color: [] as number[],
  thickness: [] as number[],
  transition: [] as number[],
};

setTraits(Border, {
  defaults: () => ({
    padding: 0.15,
    color: LINE,
    thickness: 2,
    transition: 1,
  }),
});

export const BorderLine = {};

const CHAR_WIDTH_RATIO = 0.55;

const BorderSystem: System = {
  group: 'simulation',
  update(state: State) {
    for (const eid of state.query([Border, Text, Transform])) {
      const content = Text.content[eid] ?? '';
      const fontSize = Text.fontSize[eid] || 1;
      const padding = Border.padding[eid];
      const color = Border.color[eid];
      const thickness = Border.thickness[eid];
      const t = Math.min(1, Border.transition[eid]);

      const textWidth = content.length * fontSize * CHAR_WIDTH_RATIO;
      const textHeight = fontSize;

      const w = textWidth / 2 + padding;
      const h = textHeight / 2 + padding;

      const existingLines = [...state.query([Pair(ChildOf.relation, eid), BorderLine, Line])];

      if (existingLines.length === 0) {
        for (let i = 0; i < 4; i++) {
          const lineEid = state.addEntity();
          state.addComponent(lineEid, Transform);
          state.addComponent(lineEid, Line);
          state.addComponent(lineEid, BorderLine);
          state.addComponent(lineEid, Pair(ChildOf.relation, eid));

          Line.color[lineEid] = color;
          Line.thickness[lineEid] = thickness;
          Line.visible[lineEid] = 1;
          Line.opacity[lineEid] = 1;
        }
      }

      const lines = [...state.query([Pair(ChildOf.relation, eid), BorderLine, Line])];

      // Bottom line (local coords relative to parent)
      Transform.posX[lines[0]] = -w;
      Transform.posY[lines[0]] = -h;
      Transform.posZ[lines[0]] = 0;
      Line.offsetX[lines[0]] = w * 2 * t;
      Line.offsetY[lines[0]] = 0;
      Line.offsetZ[lines[0]] = 0;

      // Top line
      Transform.posX[lines[1]] = -w;
      Transform.posY[lines[1]] = h;
      Transform.posZ[lines[1]] = 0;
      Line.offsetX[lines[1]] = w * 2 * t;
      Line.offsetY[lines[1]] = 0;
      Line.offsetZ[lines[1]] = 0;

      // Left line
      Transform.posX[lines[2]] = -w;
      Transform.posY[lines[2]] = -h;
      Transform.posZ[lines[2]] = 0;
      Line.offsetX[lines[2]] = 0;
      Line.offsetY[lines[2]] = h * 2 * t;
      Line.offsetZ[lines[2]] = 0;

      // Right line
      Transform.posX[lines[3]] = w;
      Transform.posY[lines[3]] = -h;
      Transform.posZ[lines[3]] = 0;
      Line.offsetX[lines[3]] = 0;
      Line.offsetY[lines[3]] = h * 2 * t;
      Line.offsetZ[lines[3]] = 0;
    }
  },
};

export const BorderPlugin: Plugin = {
  components: { Border, BorderLine },
  systems: [BorderSystem],
};
